import { useMemo, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { trackEvent } from '@/telemetry/trackEvent.ts';
import { useUiStore } from '@/store/uiStore.ts';
import { useAiStore } from '@/store/aiStore.ts';
import { useProjectStore } from '@/store/projectStore.ts';
import { useAudioStore } from '@/store/audioStore.ts';
import { useCommentStore } from '@/store/commentStore.ts';
import { useUndoStore } from '@/store/undoStore.ts';
import { usePlaybackStore } from '@/store/playbackStore.ts';
import { useAccountStore } from '@/store/accountStore.ts';
import { canAfford } from '@/ai/tokenCosts.ts';
import { buildVoiceMap, getVoicesForModel } from '@/types/tts.ts';
import { planScratchCredits, scratchUnitCost, chargedCredits, splitCredits } from './scratchCredits.ts';
import { extractStructuredCharacters, structuredToDialogLines } from '@/previs/screenplayHelpers.ts';
import { parseScreenplayDialog } from '@/previs/screenplayParser.ts';
import { playVoiceSample, stopVoiceSample } from './VoiceSamplePlayer.ts';
import { useMobileLayout } from '@/hooks/useMobileLayout.ts';
import { diagLog } from '@/bridge/embeddedBridge.ts';
import { getClipAbsoluteStart, resolveClipPlacement } from '@/store/projectDerived.ts';
import { capabilities } from '@/capabilities.ts';
import { DropdownPicker } from '@/components/DropdownPicker.tsx';
import { GenerationHistoryPanel } from '@/components/GenerationHistoryPanel.tsx';
import styles from './AudioWorkspace.module.css';
import aiStyles from '@/components/AiWorkspace/AiWorkspace.module.css';

const TTS_MODELS = [
  { id: 'kokoro', tierKey: 'scratchTierFast', name: 'Kokoro', estSeconds: 3 },
  { id: 'elevenlabs', tierKey: 'scratchTierBest', name: 'ElevenLabs', estSeconds: 5 },
] as const;

/** In embedded mode, wait for Unity to confirm a CDN audio clip has been downloaded and written to SQLite.
 *  Returns immediately in standalone mode (no bridge). Times out after 15s. Rejects on failure. */
function waitForAudioClipReady(clipId: number): Promise<void> {
  if (!capabilities.embeddedAudio) return Promise.resolve();
  return new Promise<void>((resolve, reject) => {
    const timeout = setTimeout(() => { cleanup(); resolve(); }, 15000);
    const readyHandler = (e: Event) => {
      if ((e as CustomEvent).detail?.clipId === clipId) { cleanup(); resolve(); }
    };
    const failHandler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.clipId === clipId) {
        cleanup();
        reject(new Error(`Audio clip ${detail.reason || 'failed'}: couldn't load the audio clip`));
      }
    };
    const cleanup = () => {
      clearTimeout(timeout);
      window.removeEventListener('__audioClipReady', readyHandler);
      window.removeEventListener('__audioClipFailed', failHandler);
    };
    window.addEventListener('__audioClipReady', readyHandler);
    window.addEventListener('__audioClipFailed', failHandler);
  });
}

interface ScratchPanelProps {
  onBusyChange?: (busy: boolean) => void;
}

export function ScratchPanel({ onBusyChange }: ScratchPanelProps = {}) {
  const { t } = useTranslation('audioWorkspace');
  const isMobile = useMobileLayout();

  const scratchType = useUiStore(s => s.scratchType);
  const setScratchType = useUiStore(s => s.setScratchType);
  const scratchScope = useUiStore(s => s.scratchScope);
  const setScratchScope = useUiStore(s => s.setScratchScope);
  const scratchScriptCoverage = useUiStore(s => s.scratchScriptCoverage);

  const aiSelectedTtsModel = useAiStore(s => s.aiSelectedTtsModel);
  const setSelectedModel = useAiStore(s => s.setSelectedModel);
  const voiceAssignments = useAiStore(s => s.voiceAssignments);
  const setVoiceAssignment = useAiStore(s => s.setVoiceAssignment);
  const retimeToMatchSpeech = useAiStore(s => s.retimeToMatchSpeech);
  const toggleRetimeToMatchSpeech = useAiStore(s => s.toggleRetimeToMatchSpeech);

  const scenes = useProjectStore(s => s.project?.scenes ?? []);
  const activeSceneId = useProjectStore(s => s.activeSceneId);
  const currentTime = usePlaybackStore(s => s.currentTime);
  const getShotAtTime = useProjectStore(s => s.getShotAtTime);

  // Derive current scene from playhead position (like V1 TtsPanel),
  // falling back to activeSceneId from the scene selector dropdown
  const { currentScene, currentShot } = useMemo(() => {
    const shot = getShotAtTime(currentTime);
    let scene = scenes[0] ?? null;
    if (shot) {
      const found = scenes.find(s => s.shots.some(sh => sh.id === shot.id));
      if (found) scene = found;
    } else if (activeSceneId !== null) {
      scene = scenes.find(s => s.id === activeSceneId) ?? scenes[0] ?? null;
    }
    return { currentScene: scene, currentShot: shot };
  }, [scenes, activeSceneId, currentTime, getShotAtTime]);

  const selectedModel = aiSelectedTtsModel;

  const characters = useMemo(() => {
    if (scratchType !== 'dialogue') return [];
    if (scratchScope === 'custom') return [];

    if (scratchScope === 'allScenes') {
      const allChars: string[] = [];
      for (const scene of scenes) {
        if (scene.screenplay?.dialogue.length) {
          const sceneChars = extractStructuredCharacters(scene.screenplay);
          for (const c of sceneChars) {
            if (!allChars.includes(c)) allChars.push(c);
          }
        }
      }
      return allChars;
    }

    if (scratchScope === 'selectedScene' && currentScene?.screenplay?.dialogue.length) {
      return extractStructuredCharacters(currentScene.screenplay);
    }

    if (scratchScope === 'selectedShot' && currentScene?.screenplay && currentShot) {
      const shotDialogue = currentScene.screenplay.dialogue.filter(d => d.shotId === currentShot.id);
      return [...new Set(shotDialogue.map(d => d.characterName))];
    }

    return [];
  }, [scratchType, scratchScope, scenes, currentScene, currentShot]);

  const voiceMap = useMemo(() => {
    const sceneId = scratchScope === 'allScenes' ? 0 : (currentScene?.id ?? 0);
    return buildVoiceMap(sceneId, selectedModel, voiceAssignments, characters);
  }, [scratchScope, currentScene, selectedModel, voiceAssignments, characters]);

  const voices = useMemo(() => getVoicesForModel(selectedModel), [selectedModel]);

  const [customText, setCustomText] = useState('');
  const [customVoice, setCustomVoice] = useState(() => voices[0]?.id ?? '');
  const [overwriteExisting, setOverwriteExisting] = useState(false);
  const [skipApproved, setSkipApproved] = useState(true);

  // Music/SFX audio generation state
  const [audioGenPrompt, setAudioGenPrompt] = useState('');
  const [audioGenDuration, setAudioGenDuration] = useState<number>(() =>
    scratchType === 'music' ? 15 : 5
  );
  // Reset duration default when switching between music/sfx
  useEffect(() => {
    setAudioGenDuration(scratchType === 'music' ? 15 : 5);
  }, [scratchType]);

  const [generating, setGenerating] = useState(false);
  const [generationStatus, setGenerationStatus] = useState('');
  const [elapsed, setElapsed] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const formatTime = (s: number) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  const startTimer = useCallback(() => {
    setElapsed(0);
    timerRef.current = setInterval(() => {
      setElapsed(prev => prev + 1);
    }, 1000);
  }, []);

  const stopTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  useEffect(() => () => stopTimer(), [stopTimer]);

  // Cancel flag for music/sfx — server request can't be aborted, so we dismiss UI immediately
  const audioGenCancelledRef = useRef(false);
  const cancelAudioGen = useCallback(() => {
    audioGenCancelledRef.current = true;
    abortRef.current?.abort();
    stopTimer();
    setGenerating(false);
    setGenerationStatus('');
  }, [stopTimer]);
  const [typeDropdownOpen, setTypeDropdownOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [modelDropdownOpen, setModelDropdownOpen] = useState(false);
  const [scopeDropdownOpen, setScopeDropdownOpen] = useState(false);
  const [voiceDropdownOpen, setVoiceDropdownOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);

  useEffect(() => {
    if (voices.length > 0 && !voices.find(v => v.id === customVoice)) {
      setCustomVoice(voices[0].id);
    }
  }, [voices, customVoice]);

  useEffect(() => { onBusyChange?.(generating); }, [generating, onBusyChange]);

  // Sync audio lane target based on scratch type
  const setTargetLane = useUiStore(s => s.setAudioWorkspaceTargetLane);
  useEffect(() => {
    const laneMap: Record<string, 0 | 1 | 2 | 3> = {
      dialogue: 0, music: 1, sfx: 2, voiceover: 3,
    };
    setTargetLane(laneMap[scratchType] ?? 0);
  }, [scratchType, setTargetLane]);

  const handleVoiceChange = useCallback((character: string, voice: string) => {
    const sceneId = scratchScope === 'allScenes' ? 0 : (currentScene?.id ?? 0);
    setVoiceAssignment(sceneId, character, selectedModel, voice);
    playVoiceSample(selectedModel as 'kokoro' | 'elevenlabs', voice);
  }, [scratchScope, currentScene, selectedModel, setVoiceAssignment]);

  useEffect(() => () => stopVoiceSample(), []);

  const hasScreenplay = scenes.some(
    sc =>
      (sc.screenplay?.dialogue?.length ?? 0) > 0 ||
      (sc.screenplayText?.trim().length ?? 0) > 0
  );

  const currentSceneHasDialogue = !!(
    (currentScene?.screenplay?.dialogue?.length ?? 0) > 0 ||
    (currentScene?.screenplayText?.trim().length ?? 0) > 0
  );

  // Set initial scope based on scene selector when workspace opens
  const setScratchScopeStore = useUiStore(s => s.setScratchScope);
  const didSetInitialScope = useRef(false);
  useEffect(() => {
    if (didSetInitialScope.current) return;
    didSetInitialScope.current = true;
    if (scratchType !== 'dialogue') return;

    if (!hasScreenplay) {
      setScratchScopeStore('custom');
    } else if (activeSceneId !== null && currentSceneHasDialogue) {
      // A specific scene is selected and has dialogue — default to Selected Scene
      setScratchScopeStore('selectedScene');
    } else if (activeSceneId !== null && !currentSceneHasDialogue) {
      // Scene selected but no dialogue — default to Custom
      setScratchScopeStore('custom');
    } else {
      // All scenes view — default to All Scenes
      setScratchScopeStore('allScenes');
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- intentionally run once on mount

  // Auto-switch to Custom scope when current state has no dialogue
  useEffect(() => {
    if (scratchType !== 'dialogue') return;
    if (!hasScreenplay && scratchScope !== 'custom') {
      setScratchScopeStore('custom');
    } else if (scratchScope === 'selectedScene' && !currentSceneHasDialogue) {
      setScratchScopeStore('custom');
    }
  }, [scratchType, hasScreenplay, currentSceneHasDialogue, scratchScope, setScratchScopeStore]);

  const isAudioGenType = scratchType === 'music' || scratchType === 'sfx';

  const dialogueCount = useMemo(() => {
    if (isAudioGenType) return audioGenPrompt.trim() ? 1 : 0;
    if (scratchType === 'voiceover' || scratchScope === 'custom') return customText.trim() ? 1 : 0;
    if (scratchType !== 'dialogue') return 0;

    if (scratchScope === 'allScenes') {
      return scenes.reduce((sum, sc) => {
        if (sc.screenplay?.dialogue.length) return sum + sc.screenplay.dialogue.length;
        return sum;
      }, 0);
    }

    if (scratchScope === 'selectedScene' && currentScene?.screenplay) {
      return currentScene.screenplay.dialogue.length;
    }

    if (scratchScope === 'selectedShot' && currentScene?.screenplay && currentShot) {
      return currentScene.screenplay.dialogue.filter(d => d.shotId === currentShot.id).length;
    }

    return 0;
  }, [scratchType, isAudioGenType, scratchScope, scenes, currentScene, currentShot, customText, audioGenPrompt]);

  const canGenerate = dialogueCount > 0;

  // All Scenes sends one batch per scene with dialogue lines (the generate loop below filters
  // freshScenes the same way), so this count drives both the button label and the estimate.
  const scenesWithDialogue = useMemo(() => {
    if (scratchScope !== 'allScenes') return 0;
    return scenes.filter(sc => sc.screenplay?.dialogue?.length).length;
  }, [scratchScope, scenes]);

  // 20261008 (owner): quote what the server deducts — a typed line is perLine, a shot/scene is
  // ONE flat perScene batch, All Scenes is perScene per scene (rule: ./scratchCredits.ts).
  const creditPlan = useMemo(() => planScratchCredits({
    scratchType,
    scratchScope,
    model: selectedModel,
    lineCount: dialogueCount,
    scenesWithDialogue,
  }), [scratchType, scratchScope, selectedModel, dialogueCount, scenesWithDialogue]);
  /** Credits the Generate button will spend; the one-request price while nothing is selected. */
  const creditEstimate = creditPlan.total || creditPlan.unitCost;

  const handleGenerate = useCallback(async () => {
    if (!canGenerate || generating) return;

    // Check credits before starting generation
    const accountBalance = useAccountStore.getState().accountBalance;
    if (accountBalance && !accountBalance.isBeta && !canAfford(accountBalance, creditEstimate)) {
      const store = useAccountStore.getState();
      if (!store.welcomeDepletionDismissedThisSession) {
        const hasPurchased = await store.checkHasEverPurchased();
        if (!hasPurchased) {
          store.setWelcomeDepletionOpen(true);
          return;
        }
      }
      trackEvent('depletion_shown', { context: 'ai_panel' });
      store.setTokenPurchaseDialogOpen(true);
      return;
    }

    const LANE_MAP: Record<string, number> = { dialogue: 0, music: 1, sfx: 2, voiceover: 3 };
    const targetLane = LANE_MAP[scratchType] ?? 0;
    const LANE_COLORS = ['#4a90d9', '#d94a8a', '#4ad97a', '#d9a54a'];

    // Telemetry: map scope to generate_tapped scope value
    const audioScopeMap: Record<string, 'single' | 'scenes' | 'all'> = {
      allScenes: 'all',
      selectedScene: 'scenes',
      selectedShot: 'single',
      custom: 'single',
    };
    const audioScope = audioScopeMap[scratchScope] ?? 'single';
    trackEvent('generate_tapped', {
      model_id: scratchType === 'music' || scratchType === 'sfx' ? scratchType : selectedModel,
      generation_type: `audio_${scratchType}`,
      scope: audioScope,
      batch_size: dialogueCount > 0 ? dialogueCount : undefined,
    });
    const audioGenStartTime = Date.now();

    // ── music / sfx branch ───────────────────────────────────────────────
    if (scratchType === 'music' || scratchType === 'sfx') {
      if (!audioGenPrompt.trim()) return;

      const audioModel = scratchType as 'music' | 'sfx';
      const clipPrice = scratchUnitCost(audioModel, scratchScope, selectedModel);

      diagLog('AudioGen', `audio-gen start — type=${audioModel} duration=${audioGenDuration} lane=${targetLane}`);

      setGenerating(true);
      setGenerationStatus(t('statusGenerating'));
      startTimer();
      audioGenCancelledRef.current = false;
      abortRef.current = new AbortController();

      try {
        const { generateAudio, createTtsAudioClip, createTtsHistoryEntry } = await import('@/ai/ttsOrchestrator.ts');

        useUndoStore.getState().pushSnapshot();

        const result = await generateAudio(
          audioGenPrompt.trim(),
          audioModel,
          audioGenDuration,
          undefined,
          abortRef.current.signal,
          (s: string) => {
            if (!audioGenCancelledRef.current) {
              setGenerationStatus(s === 'generating' ? t('statusGenerating') : t('statusDownloading'));
            }
          },
        );

        // User cancelled while server was still working — discard result
        if (audioGenCancelledRef.current) return;

        // The server's token_cost is what was deducted; the clip price is only the fallback.
        const creditCost = chargedCredits(result.tokenCost, clipPrice);
        diagLog('AudioGen', `generateAudio done — duration=${result.duration?.toFixed(2)}s cdnUrl=${result.cdnUrl ?? 'none'} tokenCost=${creditCost}`);

        const currentTime = usePlaybackStore.getState().currentTime;
        const _allocId = useProjectStore.getState()._allocId;
        const addAudioClip = useAudioStore.getState().addAudioClip;
        const addHistoryEntry = useAiStore.getState().addHistoryEntry;

        const clipId = _allocId();
        const genProject = useProjectStore.getState().project;
        const { shotId: genShotId, shotOffset: genShotOffset } = genProject
          ? resolveClipPlacement(currentTime, genProject)
          : { shotId: 0, shotOffset: currentTime };
        const clip = createTtsAudioClip(
          result,
          genShotId,
          genShotOffset,
          clipId,
          undefined,
          audioGenPrompt.trim().slice(0, 40),
        );
        clip.lane = targetLane;
        clip.color = LANE_COLORS[targetLane];
        addAudioClip(clip, { pushUndo: false });

        diagLog('AudioGen', `clip placed — clipId=${clipId} lane=${targetLane} absoluteStart=${currentTime.toFixed(2)}s duration=${result.duration?.toFixed(2)}s`);

        const shot = useProjectStore.getState().getShotAtTime(currentTime);
        const histEntry = createTtsHistoryEntry(
          result,
          shot?.id ?? null,
          audioModel,
          '',
          '',
          audioGenPrompt.trim(),
          creditCost,
          clipId,
          targetLane,
        );
        addHistoryEntry(histEntry);

        if (result.cdnUrl) {
          import('@/bridge/generationHistorySyncBridge.ts').then(m => {
            m.sendHistoryCdnUrlToUnity(histEntry.id, result.cdnUrl, 'audio/mpeg');
          });
        }

        trackEvent('generate_succeeded', {
          model_id: audioModel,
          generation_type: `audio_${audioModel}`,
          duration_ms: Date.now() - audioGenStartTime,
          credits_spent: creditCost,
        });

        // Wait for Unity to finish downloading before dismissing overlay
        await waitForAudioClipReady(clipId);
      } catch (err) {
        if (!abortRef.current?.signal.aborted) {
          const msg = err instanceof Error ? err.message : String(err);
          diagLog('AudioGen', `audio-gen failed: ${msg}`);
          setErrorMessage(msg);
        }
      } finally {
        stopTimer();
        abortRef.current = null;
        setGenerating(false);
        setGenerationStatus('');
      }
      return;
    }
    // ── end music / sfx branch ───────────────────────────────────────────

    // ── custom / voiceover branch ────────────────────────────────────────
    if (scratchScope === 'custom' || scratchType === 'voiceover') {
      if (!customText.trim()) return;

      diagLog('AudioGen', `custom/voiceover start — type=${scratchType} model=${selectedModel} voice=${customVoice} lane=${targetLane}`);

      setGenerating(true);
      setGenerationStatus(t('statusGenerating'));
      startTimer();
      abortRef.current = new AbortController();

      try {
        const { generateSingleTts, createTtsAudioClip, createTtsHistoryEntry } = await import('@/ai/ttsOrchestrator.ts');

        useUndoStore.getState().pushSnapshot();

        const result = await generateSingleTts(
          customText.trim(),
          customVoice,
          selectedModel,
          abortRef.current.signal,
          (s: string) => setGenerationStatus(s === 'generating' ? t('statusGenerating') : t('statusDownloading')),
        );

        // One submit-tts call = perLine; prefer the token_cost the server returned.
        const lineCost = chargedCredits(result.tokenCost, scratchUnitCost(scratchType, scratchScope, selectedModel));
        diagLog('AudioGen', `generateSingleTts done — duration=${result.duration?.toFixed(2)}s cdnUrl=${result.cdnUrl ?? 'none'} tokenCost=${lineCost}`);

        const currentTime = usePlaybackStore.getState().currentTime;
        const _allocId = useProjectStore.getState()._allocId;
        const addAudioClip = useAudioStore.getState().addAudioClip;
        const addHistoryEntry = useAiStore.getState().addHistoryEntry;

        const clipId = _allocId();
        const textLabel = customText.trim();
        const ttsProject = useProjectStore.getState().project;
        const { shotId: ttsShotId, shotOffset: ttsShotOffset } = ttsProject
          ? resolveClipPlacement(currentTime, ttsProject)
          : { shotId: 0, shotOffset: currentTime };
        const clip = createTtsAudioClip(
          result,
          ttsShotId,
          ttsShotOffset,
          clipId,
          undefined,
          textLabel,
        );
        clip.lane = targetLane;
        clip.color = LANE_COLORS[targetLane];
        addAudioClip(clip, { pushUndo: false });

        diagLog('AudioGen', `clip placed — clipId=${clipId} lane=${targetLane} absoluteStart=${currentTime.toFixed(2)}s duration=${result.duration?.toFixed(2)}s`);

        // Retime: extend current shot if audio exceeds it
        if (useAiStore.getState().retimeToMatchSpeech) {
          const shot = useProjectStore.getState().getShotAtTime(currentTime);
          if (shot) {
            const PADDING = 1.0;
            const needed = result.duration + PADDING * 2;
            if (needed > shot.duration) {
              useProjectStore.getState().updateShotDuration(shot.id, needed, { pushUndo: false });
            }
          }
        }

        // History entry
        const shot = useProjectStore.getState().getShotAtTime(currentTime);
        const histEntry = createTtsHistoryEntry(
          result,
          shot?.id ?? null,
          selectedModel,
          customVoice,
          '',
          textLabel,
          lineCost,
          clipId,
        );
        addHistoryEntry(histEntry);

        if (result.cdnUrl) {
          import('@/bridge/generationHistorySyncBridge.ts').then(m => {
            m.sendHistoryCdnUrlToUnity(histEntry.id, result.cdnUrl, 'audio/mpeg');
          });
        }

        trackEvent('generate_succeeded', {
          model_id: selectedModel,
          generation_type: `audio_${scratchType}`,
          duration_ms: Date.now() - audioGenStartTime,
          credits_spent: lineCost,
        });

        // In embedded mode, wait for Unity to finish downloading the CDN audio
        // before dismissing the loading overlay — otherwise playback may fail
        // because the audio blob hasn't been written to SQLite yet.
        await waitForAudioClipReady(clipId);
      } catch (err) {
        if (!abortRef.current?.signal.aborted) {
          const msg = err instanceof Error ? err.message : String(err);
          diagLog('AudioGen', `custom/voiceover generation failed: ${msg}`);
          setErrorMessage(msg);
        }
      } finally {
        stopTimer();
        abortRef.current = null;
        setGenerating(false);
        setGenerationStatus('');
      }
      return;
    }
    // ── end custom / voiceover branch ────────────────────────────────────

    setGenerating(true);
    startTimer();
    abortRef.current = new AbortController();

    try {
      const { generateSceneTts, createTtsAudioClip, createTtsHistoryEntry } = await import('@/ai/ttsOrchestrator.ts');
      const { decodeAudio, generatePeaks } = await import('@/audio/audioProcessor.ts');

      const freshScenes = useProjectStore.getState().project?.scenes ?? [];

      interface DecodedClip {
        r: Awaited<ReturnType<typeof generateSceneTts>>['results'][number];
        rawData: Uint8Array;
        audioBuffer: AudioBuffer;
        peaks: Float32Array;
      }

      // ── selectedShot branch ──────────────────────────────────────────────
      if (scratchScope === 'selectedShot') {
        const freshScene = freshScenes.find(s => s.id === currentScene?.id) ?? null;
        const freshShot = freshScene?.shots.find(sh => sh.id === currentShot?.id) ?? null;

        if (!freshScene || !freshShot || !freshScene.screenplay) return;

        const shotDialogue = freshScene.screenplay.dialogue.filter(d => d.shotId === freshShot.id);
        if (shotDialogue.length === 0) return;

        diagLog('AudioGen', `selectedShot start — sceneId=${freshScene.id} shotId=${freshShot.id} dialogueLines=${shotDialogue.length}`);

        // ── overwriteExisting / skipApproved filtering ───────────────────
        // For selectedShot: the entire shot is the unit. Check at the shot level.
        if (!overwriteExisting) {
          // Skip if the shot already has any audio clips on lane 0 (Dialog)
          const shotStart = useProjectStore.getState().getShotStartTime(freshShot.id);
          const shotEnd = shotStart + freshShot.duration;
          const existingClips = useAudioStore.getState().audioClips;
          const freshProject = useProjectStore.getState().project!;
          const hasDialogAudio = existingClips.some(c => {
            const absStart = getClipAbsoluteStart(c, freshProject);
            return c.lane === targetLane && absStart < shotEnd && (absStart + c.duration) > shotStart;
          });
          if (hasDialogAudio) {
            diagLog('AudioGen', `selectedShot skipped — overwriteExisting=false and audio already on lane ${targetLane}`);
            return;
          }
        }

        if (scratchScriptCoverage && skipApproved) {
          // Skip if the shot is approved
          const approval = useCommentStore.getState().getApprovalForShot(freshShot.id);
          if (approval?.status === 'approved') {
            diagLog('AudioGen', `selectedShot skipped — shot is approved and skipApproved=true`);
            return;
          }
        }
        // ── end filtering ────────────────────────────────────────────────

        const currentVoiceAssignments = useAiStore.getState().voiceAssignments;
        const _allocId = useProjectStore.getState()._allocId;
        const addAudioClip = useAudioStore.getState().addAudioClip;
        const addHistoryEntry = useAiStore.getState().addHistoryEntry;

        useUndoStore.getState().pushSnapshot();

        // The shot's lines go out as ONE batch: a flat perScene charge, whatever the line count.
        const batchPrice = scratchUnitCost(scratchType, scratchScope, selectedModel);
        const GAP = 0.3;
        const PADDING = 1.0;

        const shotCharacters = [...new Set(shotDialogue.map(d => d.characterName))];
        const shotVoiceMap = buildVoiceMap(freshScene.id, selectedModel, currentVoiceAssignments, shotCharacters);

        const dialogLines = shotDialogue
          .slice()
          .sort((a, b) => a.scriptOrder - b.scriptOrder)
          .map(d => ({
            text: d.dialogueText,
            voice: shotVoiceMap.get(d.characterName) ?? voiceMap.get(d.characterName) ?? voices[0]?.id ?? 'af_heart',
            character: d.characterName,
          }));

        setGenerationStatus(t('statusSceneProgress', {
          current: 1,
          total: 1,
          scene: freshScene.name ?? String(freshScene.id),
        }));

        try {
          const result = await generateSceneTts({
            dialogLines,
            model: selectedModel,
            abortSignal: abortRef.current?.signal,
            onProgress: (current, total) => {
              setGenerationStatus(t('statusLinesProgress', { current, total }));
            },
          });

          const shotCharged = chargedCredits(result.totalTokenCost, batchPrice);
          diagLog('AudioGen', `selectedShot generateSceneTts done — results=${result.results.length} tokenCost=${shotCharged} (flat per batch)`);

          const shotStart = useProjectStore.getState().getShotStartTime(freshShot.id);
          let placementTime = shotStart + PADDING;

          const successResults = result.results.filter(r => r.success && r.audioUrl);
          const decodePromises = successResults.map(async (r): Promise<DecodedClip | null> => {
            try {
              const resp = await fetch(r.audioUrl!);
              const buf = await resp.arrayBuffer();
              const rawData = new Uint8Array(buf);
              const audioBuffer = await decodeAudio(rawData);
              const peaks = generatePeaks(audioBuffer);
              diagLog('AudioGen', `decoded clip — char=${r.character} text="${r.text.slice(0, 40)}" duration=${audioBuffer.duration.toFixed(2)}s cdnUrl=${r.cdnUrl ?? 'none'}`);
              return { r, rawData, audioBuffer, peaks };
            } catch (decErr) {
              diagLog('AudioGen', `failed to decode clip for "${r.character}": ${String(decErr)}`);
              return null;
            }
          });
          const decoded = (await Promise.all(decodePromises)).filter((d): d is DecodedClip => d !== null);

          let lastClipId = 0;
          if (decoded.length > 0) {
            let totalAudioDuration = PADDING;
            for (const d of decoded) {
              totalAudioDuration += d.audioBuffer.duration + GAP;
            }
            totalAudioDuration += PADDING - GAP;

            // Retime: expand the single shot if audio exceeds shot duration
            if (useAiStore.getState().retimeToMatchSpeech) {
              const latestShot = useProjectStore.getState().project?.scenes
                .find(s => s.id === freshScene.id)?.shots.find(sh => sh.id === freshShot.id);
              if (latestShot && totalAudioDuration > latestShot.duration) {
                useProjectStore.getState().updateShotDuration(latestShot.id, totalAudioDuration, { pushUndo: false });
              }
            }

            // Place clips — the batch's charge is spread over its clips so history sums to it
            const shotClipCosts = splitCredits(shotCharged, decoded.length);
            for (const [clipIndex, { r, rawData, audioBuffer, peaks }] of decoded.entries()) {
              const clipId = _allocId();
              lastClipId = clipId;
              const shotBatchProject = useProjectStore.getState().project;
              const { shotId: sbShotId, shotOffset: sbShotOffset } = shotBatchProject
                ? resolveClipPlacement(placementTime, shotBatchProject)
                : { shotId: 0, shotOffset: placementTime };
              const clip = createTtsAudioClip(
                { ...r, rawData, audioBuffer, peaks, duration: audioBuffer.duration, seed: null },
                sbShotId,
                sbShotOffset,
                clipId,
                r.character,
                r.text,
              );
              clip.lane = targetLane;
              clip.color = LANE_COLORS[targetLane];
              addAudioClip(clip, { pushUndo: false });

              diagLog('AudioGen', `clip placed — clipId=${clipId} lane=${targetLane} absoluteStart=${placementTime.toFixed(2)}s duration=${audioBuffer.duration.toFixed(2)}s`);

              const histEntry = createTtsHistoryEntry(
                { ...r, rawData, audioBuffer, peaks, duration: audioBuffer.duration, seed: null },
                null,
                selectedModel,
                shotVoiceMap.get(r.character) ?? voiceMap.get(r.character) ?? '',
                r.character,
                r.text,
                shotClipCosts[clipIndex],
                clipId,
              );
              addHistoryEntry(histEntry);

              if (r.cdnUrl) {
                import('@/bridge/generationHistorySyncBridge.ts').then(m => {
                  m.sendHistoryCdnUrlToUnity(histEntry.id, r.cdnUrl!, 'audio/mpeg');
                });
              }

              placementTime += audioBuffer.duration + GAP;
            }
          }

          // Wait for the last clip's CDN download to finish before dismissing overlay
          if (lastClipId > 0) {
            trackEvent('generate_succeeded', {
              model_id: selectedModel,
              generation_type: 'audio_dialogue',
              duration_ms: Date.now() - audioGenStartTime,
              credits_spent: shotCharged,
            });
            await waitForAudioClipReady(lastClipId);
          }
        } catch (shotErr) {
          if (!abortRef.current?.signal.aborted) {
            const msg = shotErr instanceof Error ? shotErr.message : String(shotErr);
            diagLog('AudioGen', `selectedShot generation failed: ${msg}`);
            setErrorMessage(msg);
          }
        }

        return;
      }
      // ── end selectedShot branch ──────────────────────────────────────────

      // Determine which scenes to process (allScenes / selectedScene)
      const scenesToProcess = scratchScope === 'allScenes'
        ? freshScenes.filter(sc => (sc.screenplay?.dialogue?.length ?? 0) > 0)
        : (currentScene ? [freshScenes.find(s => s.id === currentScene.id) ?? currentScene] : []);

      if (scenesToProcess.length === 0) return;

      diagLog('AudioGen', `batch start — scope=${scratchScope} scenes=${scenesToProcess.length}`);

      const currentVoiceAssignments = useAiStore.getState().voiceAssignments;
      const { setVoiceAssignment: saveVoice } = useAiStore.getState();
      const _allocId = useProjectStore.getState()._allocId;
      const addAudioClip = useAudioStore.getState().addAudioClip;
      const addHistoryEntry = useAiStore.getState().addHistoryEntry;

      useUndoStore.getState().pushSnapshot();

      // Each scene below is ONE batch call: a flat perScene charge per scene sent.
      const batchPrice = scratchUnitCost(scratchType, scratchScope, selectedModel);
      const GAP = 0.3;
      const PADDING = 1.0;

      // Compute cumulative scene start offsets
      let cumulativeStart = 0;
      const sceneStartMap = new Map<number, number>();
      for (const sc of freshScenes) {
        sceneStartMap.set(sc.id, cumulativeStart);
        cumulativeStart += sc.shots.reduce((sum, sh) => sum + sh.duration, 0);
      }

      let totalClipsPlaced = 0;
      let lastBatchClipId = 0;
      let creditsCharged = 0;

      for (let si = 0; si < scenesToProcess.length; si++) {
        const scene = scenesToProcess[si];

        if (abortRef.current?.signal.aborted) break;

        setGenerationStatus(t('statusSceneProgress', {
          current: si + 1,
          total: scenesToProcess.length,
          // `??` is dead here: scene.name is a required string, so '' (no location set) never hits
          // the nullish fallback. `||` actually fires the id fallback for a location-less scene.
          scene: scene.name || String(scene.id),
        }));

        diagLog('AudioGen', `scene [${si + 1}/${scenesToProcess.length}] start — id=${scene.id} name="${scene.name ?? ''}"`);

        // Build dialog lines for this scene
        let dialogLines: Array<{ text: string; voice: string; character: string }>;
        let sceneCharacters: string[];

        if (scene.screenplay?.dialogue.length) {
          // ── overwriteExisting / skipApproved filtering (structured path) ──
          // Filter at the ScreenplayDialogue level (has shotId) before converting.
          const existingAudioClips = useAudioStore.getState().audioClips;
          const getApprovalForShot = useCommentStore.getState().getApprovalForShot;
          const batchProject = useProjectStore.getState().project!;
          const sceneStart = sceneStartMap.get(scene.id) ?? 0;

          // Build a per-shot time-range lookup for this scene
          const shotTimeMap = new Map<number, { start: number; end: number }>();
          let shotCursor = sceneStart;
          for (const sh of scene.shots) {
            shotTimeMap.set(sh.id, { start: shotCursor, end: shotCursor + sh.duration });
            shotCursor += sh.duration;
          }

          const filteredDialogue = scene.screenplay.dialogue.filter(d => {
            // Unassigned dialogue (shotId=0): cannot check audio/approval — always include
            if (d.shotId === 0) return true;

            if (!overwriteExisting) {
              const range = shotTimeMap.get(d.shotId);
              if (range) {
                const hasDialogAudio = existingAudioClips.some(c => {
                  const absStart = getClipAbsoluteStart(c, batchProject);
                  return c.lane === targetLane && absStart < range.end && (absStart + c.duration) > range.start;
                });
                if (hasDialogAudio) return false;
              }
            }

            if (scratchScriptCoverage && skipApproved) {
              const approval = getApprovalForShot(d.shotId);
              if (approval?.status === 'approved') return false;
            }

            return true;
          });

          diagLog('AudioGen', `scene ${scene.id} filtered — ${scene.screenplay.dialogue.length} total → ${filteredDialogue.length} after overwrite/skipApproved`);

          if (filteredDialogue.length === 0) continue;

          // Work with filtered screenplay for voice/character extraction
          const filteredScreenplay = { ...scene.screenplay, dialogue: filteredDialogue };
          const rawLines = structuredToDialogLines(filteredScreenplay);
          sceneCharacters = extractStructuredCharacters(filteredScreenplay);
          // ── end filtering ─────────────────────────────────────────────

          // For allScenes scope: build per-scene voice map using global assignments as fallback
          const sceneVoiceMap = buildVoiceMap(
            scratchScope === 'allScenes' ? scene.id : (currentScene?.id ?? scene.id),
            selectedModel,
            currentVoiceAssignments,
            sceneCharacters,
          );

          // For allScenes: persist global assignments down to each scene
          if (scratchScope === 'allScenes') {
            for (const [char, voiceId] of voiceMap) {
              if (sceneCharacters.includes(char)) {
                // Save as scene-specific so future generations use same voice
                saveVoice(scene.id, char, selectedModel, voiceId);
              }
            }
          }

          dialogLines = rawLines.map(dl => ({
            text: dl.text,
            voice: sceneVoiceMap.get(dl.character) ?? voiceMap.get(dl.character) ?? voices[0]?.id ?? 'af_heart',
            character: dl.character,
          }));
        } else if (scene.screenplayText) {
          const rawLines = parseScreenplayDialog(scene.screenplayText);
          sceneCharacters = [...new Set(rawLines.map(l => l.character))];
          const sceneVoiceMap = buildVoiceMap(scene.id, selectedModel, currentVoiceAssignments, sceneCharacters);
          dialogLines = rawLines.map(dl => ({
            text: dl.text,
            voice: sceneVoiceMap.get(dl.character) ?? voices[0]?.id ?? 'af_heart',
            character: dl.character,
          }));
        } else {
          continue;
        }

        if (dialogLines.length === 0) continue;

        try {
          const result = await generateSceneTts({
            dialogLines,
            model: selectedModel,
            abortSignal: abortRef.current?.signal,
            onProgress: (current, total) => {
              setGenerationStatus(t('statusLinesProgress', { current, total }));
            },
          });

          const successCount = result.results.filter(r => r.success).length;
          const withAudioUrl = result.results.filter(r => r.audioUrl).length;
          const withBuffer = result.results.filter(r => r.audioBuffer).length;
          const withRawData = result.results.filter(r => r.rawData).length;
          // The server refunds a batch only when EVERY line failed, so a batch with any
          // successful line was billed its token_cost (flat perScene).
          const sceneCharged = successCount > 0 ? chargedCredits(result.totalTokenCost, batchPrice) : 0;
          creditsCharged += sceneCharged;
          diagLog('AudioGen', `scene ${scene.id} generateSceneTts done — results=${result.results.length} success=${successCount} withAudioUrl=${withAudioUrl} withBuffer=${withBuffer} withRawData=${withRawData} tokenCost=${sceneCharged} (flat per batch)`);

          const sceneStartOffset = sceneStartMap.get(scene.id) ?? 0;
          let placementTime = sceneStartOffset + PADDING;

          // Use already-decoded results from the orchestrator (they come back with audioBuffer + rawData)
          const successResults = result.results.filter(r => r.success && (r.audioBuffer || r.audioUrl));
          diagLog('AudioGen', `scene ${scene.id} — ${successResults.length} clips to place`);

          const decoded: DecodedClip[] = [];
          for (const r of successResults) {
            try {
              let rawData = r.rawData;
              let audioBuffer = r.audioBuffer;
              let peaks = r.peaks;

              // If orchestrator already decoded (normal path), use those directly
              if (audioBuffer && rawData && peaks) {
                decoded.push({ r, rawData, audioBuffer, peaks });
                diagLog('AudioGen', `using pre-decoded clip — char=${r.character} text="${r.text.slice(0, 40)}" duration=${audioBuffer.duration.toFixed(2)}s`);
                continue;
              }

              // Fallback: fetch + decode from audioUrl/cdnUrl
              const url = r.audioUrl ?? r.cdnUrl;
              if (!url) { diagLog('AudioGen', `skip clip "${r.character}" — no audioUrl or cdnUrl`); continue; }
              diagLog('AudioGen', `fallback fetch for "${r.character}" from ${url.slice(0, 60)}`);
              const resp = await fetch(url);
              const buf = await resp.arrayBuffer();
              rawData = new Uint8Array(buf);
              audioBuffer = await decodeAudio(rawData);
              peaks = generatePeaks(audioBuffer);
              decoded.push({ r, rawData, audioBuffer, peaks });
            } catch (decErr) {
              diagLog('AudioGen', `failed to decode clip for "${r.character}": ${String(decErr)}`);
            }
          }

          diagLog('AudioGen', `scene ${scene.id} — ${decoded.length} decoded clips ready to place`);
          if (decoded.length === 0) continue;

          let totalAudioDuration = PADDING;
          for (const d of decoded) {
            totalAudioDuration += d.audioBuffer.duration + GAP;
          }
          if (decoded.length > 0) totalAudioDuration += PADDING - GAP;

          // Retime: expand shots in the scene if audio exceeds scene duration
          if (useAiStore.getState().retimeToMatchSpeech) {
            const freshScene = useProjectStore.getState().project?.scenes.find(s => s.id === scene.id);
            if (freshScene) {
              const currentTotal = freshScene.shots.reduce((sum, s) => sum + s.duration, 0);
              if (totalAudioDuration > currentTotal && freshScene.shots.length > 0) {
                const extraPerShot = (totalAudioDuration - currentTotal) / freshScene.shots.length;
                diagLog('AudioGen', `retime scene ${scene.id} — totalAudio=${totalAudioDuration.toFixed(2)}s currentTotal=${currentTotal.toFixed(2)}s extraPerShot=${extraPerShot.toFixed(2)}s`);
                for (const sh of freshScene.shots) {
                  useProjectStore.getState().updateShotDuration(sh.id, sh.duration + extraPerShot, { pushUndo: false });
                }
              }
            }
          }

          // Place clips — this scene's charge is spread over its clips so history sums to it
          const sceneClipCosts = splitCredits(sceneCharged, decoded.length);
          for (const [clipIndex, { r, rawData, audioBuffer, peaks }] of decoded.entries()) {
            const clipId = _allocId();
            const batchProject = useProjectStore.getState().project;
            const { shotId: batchShotId, shotOffset: batchShotOffset } = batchProject
              ? resolveClipPlacement(placementTime, batchProject)
              : { shotId: 0, shotOffset: placementTime };
            const clip = createTtsAudioClip(
              { ...r, rawData, audioBuffer, peaks, duration: audioBuffer.duration, seed: null },
              batchShotId,
              batchShotOffset,
              clipId,
              r.character,
              r.text,
            );
            clip.lane = targetLane;
            clip.color = LANE_COLORS[targetLane];
            addAudioClip(clip, { pushUndo: false });

            diagLog('AudioGen', `clip placed — clipId=${clipId} lane=${targetLane} absoluteStart=${placementTime.toFixed(2)}s duration=${audioBuffer.duration.toFixed(2)}s`);
            totalClipsPlaced++;

            const histEntry = createTtsHistoryEntry(
              { ...r, rawData, audioBuffer, peaks, duration: audioBuffer.duration, seed: null },
              null,
              selectedModel,
              voiceMap.get(r.character) ?? '',
              r.character,
              r.text,
              sceneClipCosts[clipIndex],
              clipId,
            );
            addHistoryEntry(histEntry);

            if (r.cdnUrl) {
              import('@/bridge/generationHistorySyncBridge.ts').then(m => {
                m.sendHistoryCdnUrlToUnity(histEntry.id, r.cdnUrl!, 'audio/mpeg');
              });
            }

            lastBatchClipId = clipId;
            placementTime += audioBuffer.duration + GAP;
          }
        } catch (sceneErr) {
          if (abortRef.current?.signal.aborted) break;
          const msg = sceneErr instanceof Error ? sceneErr.message : String(sceneErr);
          // A location-less scene (name === '') must not collapse the identifier out of the error
          // banner/log entirely ("": <message>") — fall back to the scene id, mirroring the
          // progress-status fallback above.
          const sceneLabel = scene.name || String(scene.id);
          diagLog('AudioGen', `scene "${sceneLabel}" generation failed: ${msg}`);
          setErrorMessage(prev => prev ? `${prev}\n${sceneLabel}: ${msg}` : `${sceneLabel}: ${msg}`);
          // Continue with remaining scenes
        }
      }

      diagLog('AudioGen', `batch complete — totalClipsPlaced=${totalClipsPlaced} creditsCharged=${creditsCharged}`);

      if (totalClipsPlaced > 0) {
        trackEvent('generate_succeeded', {
          model_id: selectedModel,
          generation_type: 'audio_dialogue',
          duration_ms: Date.now() - audioGenStartTime,
          credits_spent: creditsCharged,
        });
      }

      // Wait for the last clip's CDN download to finish before dismissing overlay
      if (lastBatchClipId > 0) await waitForAudioClipReady(lastBatchClipId);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      diagLog('AudioGen', `batch generation failed: ${msg}`);
      setErrorMessage(msg);
    } finally {
      stopTimer();
      abortRef.current = null;
      setGenerating(false);
      setGenerationStatus('');
    }
  }, [
    canGenerate,
    generating,
    scratchScope,
    scratchType,
    scratchScriptCoverage,
    customText,
    customVoice,
    audioGenPrompt,
    audioGenDuration,
    overwriteExisting,
    skipApproved,
    currentScene,
    currentShot,
    scenes,
    selectedModel,
    voiceMap,
    voices,
    creditEstimate,
    t,
  ]);

  const selectedTtsModel = TTS_MODELS.find(m => m.id === selectedModel) ?? TTS_MODELS[1];
  const selectedTierLabel = t(selectedTtsModel.tierKey);
  // 20261008 (owner): the model pill and its tier cards quote what the CURRENT scope costs on
  // each model — the same figure as the Generate button, so a 3-scene batch reads 30/45, not 2/3.
  const quoteForModel = (modelId: string) => {
    const plan = planScratchCredits({
      scratchType,
      scratchScope,
      model: modelId,
      lineCount: dialogueCount,
      scenesWithDialogue,
    });
    return plan.total || plan.unitCost;
  };
  const selectedModelCost = creditEstimate;

  const TYPE_LABEL_MAP: Record<string, string> = {
    dialogue: t('scratchTypeDialogue'),
    music: t('scratchTypeMusic'),
    sfx: t('scratchTypeSfx'),
    voiceover: t('scratchTypeVoiceover'),
  };
  const SCOPE_LABEL_MAP: Record<string, string> = {
    allScenes: t('scratchScopeAllScenes'),
    selectedScene: t('scratchScopeSelectedScene'),
    selectedShot: t('scratchScopeSelectedShot'),
    custom: t('scratchScopeCustom'),
  };

  const generateLabel = useMemo(() => {
    if (scratchType === 'music') return t('generateMusic');
    if (scratchType === 'sfx') return t('generateSfx');
    if (scratchType === 'voiceover') return t('generateVoiceover');
    if (scratchScope === 'custom') return t('generateDialogue');
    if (scratchScope === 'selectedShot') return t('generateShotDialogue');
    if (scratchScope === 'selectedScene') return t('generateSceneDialogue');
    // allScenes
    if (scenesWithDialogue <= 1) return t('generateSceneDialogue');
    return t('generateNSceneDialogue', { count: scenesWithDialogue });
  }, [scratchType, scratchScope, scenesWithDialogue, t]);

  return (
    <div className={styles.scratchPanel} style={isMobile ? undefined : { overflowY: 'hidden' }}>
      {/* ── 1. Compact pill row ─────────────────────────────── */}
      <div className={styles.scratchPillRow}>
        {/* Type pill — always shown */}
        <div className={styles.scratchPillWrapper}>
          <DropdownPicker
            selectedLabel={TYPE_LABEL_MAP[scratchType] ?? scratchType}
            open={typeDropdownOpen}
            onToggle={() => setTypeDropdownOpen(o => !o)}
          >
            <div className={styles.scratchPickerList}>
              <button
                className={`${styles.scratchPickerItem} ${scratchType === 'dialogue' ? styles.scratchPickerItemSelected : ''}`}
                onClick={() => { setScratchType('dialogue'); setTypeDropdownOpen(false); }}
              >
                {t('scratchTypeDialogue')}
              </button>
              <button
                className={`${styles.scratchPickerItem} ${scratchType === 'music' ? styles.scratchPickerItemSelected : ''}`}
                onClick={() => { setScratchType('music'); setTypeDropdownOpen(false); }}
              >
                {t('scratchTypeMusic')}
              </button>
              <button
                className={`${styles.scratchPickerItem} ${scratchType === 'sfx' ? styles.scratchPickerItemSelected : ''}`}
                onClick={() => { setScratchType('sfx'); setTypeDropdownOpen(false); }}
              >
                {t('scratchTypeSfx')}
              </button>
              <button
                className={`${styles.scratchPickerItem} ${scratchType === 'voiceover' ? styles.scratchPickerItemSelected : ''}`}
                onClick={() => { setScratchType('voiceover'); setTypeDropdownOpen(false); }}
              >
                {t('scratchTypeVoiceover')}
              </button>
            </div>
          </DropdownPicker>
        </div>

        {/* Model pill — hidden for music/sfx */}
        {!isAudioGenType && (
          <div className={styles.scratchPillWrapper}>
            <DropdownPicker
              selectedLabel={`${selectedTierLabel} ${selectedModelCost}${t('creditSuffix')}`}
              open={modelDropdownOpen}
              onToggle={() => setModelDropdownOpen(o => !o)}
            >
              <div className={aiStyles.tierCards}>
                {TTS_MODELS.map(m => {
                  const selected = m.id === selectedModel;
                  const mCost = quoteForModel(m.id);
                  return (
                    <button
                      key={m.id}
                      className={`${aiStyles.tierCard} ${selected ? aiStyles.tierCardSelected : ''}`}
                      onClick={() => {
                        setSelectedModel('tts', m.id);
                        setModelDropdownOpen(false);
                      }}
                    >
                      <div className={aiStyles.tierHeader}>
                        <span className={aiStyles.tierLabel}>{t(m.tierKey)}</span>
                        <span className={aiStyles.tierCost}>{mCost} {t('creditSuffix')}</span>
                      </div>
                      <div className={aiStyles.tierSpeed}>
                        <div className={aiStyles.tierSpeedFill} style={{ width: selected ? '100%' : '60%' }} />
                      </div>
                      <div className={aiStyles.tierMeta}>
                        <span>{m.name}</span>
                        <span>~{m.estSeconds}s</span>
                      </div>
                    </button>
                  );
                })}
              </div>
              <button
                type="button"
                onClick={() => {
                  setModelDropdownOpen(false);
                  setHistoryOpen(true);
                }}
                style={{
                  display: 'block', width: '100%', padding: '8px 10px',
                  background: 'transparent',
                  border: 'none', borderRadius: 4,
                  borderTop: '1px solid var(--chrome-overlay-weak)',
                  color: 'var(--text-secondary)',
                  fontSize: 12, textAlign: 'left', cursor: 'pointer',
                  marginTop: 2,
                }}
              >
                {t('useSavedAudio')}
              </button>
            </DropdownPicker>
          </div>
        )}

        {/* Scope pill — only for dialogue */}
        {scratchType === 'dialogue' && (
          <div className={styles.scratchPillWrapper}>
            <DropdownPicker
              selectedLabel={SCOPE_LABEL_MAP[scratchScope] ?? scratchScope}
              open={scopeDropdownOpen}
              onToggle={() => setScopeDropdownOpen(o => !o)}
            >
              <div className={styles.scratchPickerList}>
                <button
                  className={`${styles.scratchPickerItem} ${scratchScope === 'allScenes' ? styles.scratchPickerItemSelected : ''} ${!hasScreenplay ? styles.scratchPickerItemDisabled : ''}`}
                  onClick={() => { if (hasScreenplay) { setScratchScope('allScenes'); setScopeDropdownOpen(false); } }}
                >
                  {t('scratchScopeAllScenes')}
                </button>
                <button
                  className={`${styles.scratchPickerItem} ${scratchScope === 'selectedScene' ? styles.scratchPickerItemSelected : ''} ${!hasScreenplay ? styles.scratchPickerItemDisabled : ''}`}
                  onClick={() => { if (hasScreenplay) { setScratchScope('selectedScene'); setScopeDropdownOpen(false); } }}
                >
                  {t('scratchScopeSelectedScene')}
                </button>
                {scratchScriptCoverage && (
                  <button
                    className={`${styles.scratchPickerItem} ${scratchScope === 'selectedShot' ? styles.scratchPickerItemSelected : ''}`}
                    onClick={() => { setScratchScope('selectedShot'); setScopeDropdownOpen(false); }}
                  >
                    {t('scratchScopeSelectedShot')}
                  </button>
                )}
                <button
                  className={`${styles.scratchPickerItem} ${scratchScope === 'custom' ? styles.scratchPickerItemSelected : ''}`}
                  onClick={() => { setScratchScope('custom'); setScopeDropdownOpen(false); }}
                >
                  {t('scratchScopeCustom')}
                </button>
              </div>
            </DropdownPicker>
          </div>
        )}

        {/* Voice pill — shown for custom dialogue / voiceover */}
        {!isAudioGenType && (scratchScope === 'custom' || scratchType === 'voiceover') && (
          <div className={styles.scratchPillWrapper}>
            <DropdownPicker
              selectedLabel={voices.find(v => v.id === customVoice)?.name ?? customVoice}
              open={voiceDropdownOpen}
              onToggle={() => setVoiceDropdownOpen(o => !o)}
            >
              <div className={styles.scratchPickerList}>
                {voices.map(v => (
                  <button
                    key={v.id}
                    className={`${styles.scratchPickerItem} ${v.id === customVoice ? styles.scratchPickerItemSelected : ''}`}
                    onClick={() => {
                      setCustomVoice(v.id);
                      playVoiceSample(selectedModel as 'kokoro' | 'elevenlabs', v.id);
                      setVoiceDropdownOpen(false);
                    }}
                  >
                    {v.name}
                  </button>
                ))}
              </div>
            </DropdownPicker>
          </div>
        )}
      </div>

      {/* ── 2. Content area (flex: 1, scrollable) ──────────── */}
      <div className={styles.scratchBody}>
        {scratchType === 'dialogue' && scratchScope !== 'custom' && characters.length > 0 && (
          <>
            <div className={styles.castLabel}>{t('castLabel')}</div>
            <div className={styles.castList}>
              {characters.map(char => (
                <div key={char} className={styles.castRow}>
                  <span className={styles.castName}>{char}</span>
                  <select
                    className={styles.castVoiceSelect}
                    value={voiceMap.get(char) ?? ''}
                    onChange={e => handleVoiceChange(char, e.target.value)}
                  >
                    {voices.map(v => (
                      <option key={v.id} value={v.id}>{v.name}</option>
                    ))}
                  </select>
                  <button
                    className={styles.castSampleBtn}
                    onClick={() => playVoiceSample(selectedModel as 'kokoro' | 'elevenlabs', voiceMap.get(char) ?? voices[0].id)}
                    title={t('previewVoice')}
                  >
                    🔊
                  </button>
                </div>
              ))}
            </div>
          </>
        )}

        {scratchType === 'dialogue' && scratchScope !== 'custom' && characters.length === 0 && (
          <div className={styles.emptyState}>
            {scratchScope === 'selectedShot'
              ? t('noDialogueForShot')
              : scratchScope === 'selectedScene'
                ? t('noDialogueForScene')
                : t('noDialogueInProject')}
          </div>
        )}

        {isAudioGenType && (
          <div className={styles.customBody}>
            <textarea
              className={styles.customTextarea}
              value={audioGenPrompt}
              onChange={e => setAudioGenPrompt(e.target.value)}
              placeholder={scratchType === 'music' ? t('musicPromptPlaceholder') : t('sfxPromptPlaceholder')}
              rows={3}
            />
          </div>
        )}

        {!isAudioGenType && (scratchScope === 'custom' || scratchType === 'voiceover') && (
          <textarea
            className={styles.customTextarea}
            value={customText}
            onChange={e => setCustomText(e.target.value)}
            placeholder={t('customTextPlaceholder')}
            rows={5}
          />
        )}
      </div>

      {/* ── 3. Bottom action bar ───────────────────────────── */}
      <div className={styles.scratchActionBar}>
        {isAudioGenType ? (
          <>
            <label className={styles.scratchCheckbox} style={{ whiteSpace: 'nowrap' }}>
              <input type="checkbox" checked={retimeToMatchSpeech} onChange={toggleRetimeToMatchSpeech} />
              {t('retimeLabel')}
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
              {t('durationLabel')}
              <select
                value={audioGenDuration}
                onChange={e => setAudioGenDuration(Number(e.target.value))}
                style={{
                  background: 'var(--bg-elevated)', color: 'var(--text-primary)',
                  border: '1px solid var(--border-color)', borderRadius: 4,
                  padding: '2px 4px', fontSize: 12, width: 52,
                }}
              >
                {(scratchType === 'music'
                  ? [3, 5, 7, 9, 11, 13, 15]
                  : [1, 3, 5, 7, 9, 11, 13, 15, 17, 19, 22]
                ).map(v => <option key={v} value={v}>{v}s</option>)}
              </select>
            </label>
          </>
        ) : (
          <>
            <label className={styles.scratchCheckbox} style={{ whiteSpace: 'nowrap' }}>
              <input type="checkbox" checked={retimeToMatchSpeech} onChange={toggleRetimeToMatchSpeech} />
              {t('retimeLabel')}
            </label>
            {scratchType === 'dialogue' && scratchScope !== 'custom' && (
              <label className={styles.scratchCheckbox} style={{ whiteSpace: 'nowrap' }}>
                <input type="checkbox" checked={overwriteExisting} onChange={e => setOverwriteExisting(e.target.checked)} />
                {t('overwriteExistingLabel')}
              </label>
            )}
            {scratchType === 'dialogue' && scratchScope !== 'custom' && scratchScriptCoverage && (
              <label className={styles.scratchCheckbox} style={{ whiteSpace: 'nowrap' }}>
                <input type="checkbox" checked={skipApproved} onChange={e => setSkipApproved(e.target.checked)} />
                {t('skipApprovedLabel')}
              </label>
            )}
          </>
        )}

        <div className={styles.scratchActionBarRight}>
          <button
            className={styles.scratchGenerateBtn}
            disabled={!canGenerate || (generating && !isAudioGenType)}
            onClick={generating && isAudioGenType ? cancelAudioGen : handleGenerate}
          >
            {generating && isAudioGenType ? (
              <>
                {generationStatus} {formatTime(elapsed)}
                <span style={{ marginLeft: 6, opacity: 0.7 }}>✕</span>
              </>
            ) : (
              <>
                <span className={styles.sparkle}>✦</span>
                {generateLabel}
                <span className={styles.scratchCreditBadge}>~{creditEstimate} cr</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* ── 4. Overlays (unchanged) ────────────────────────── */}
      {generating && !isAudioGenType && (
        <div className={styles.ttsGeneratingOverlay}>
          <div className={styles.ttsGeneratingStatus}>{generationStatus}</div>
          <div className={styles.ttsGeneratingTimer}>{formatTime(elapsed)}</div>
          <button
            className={styles.ttsGeneratingCancel}
            onClick={() => abortRef.current?.abort()}
          >
            {t('cancelGeneration')}
          </button>
        </div>
      )}

      {errorMessage && (
        <div className={styles.errorOverlay} onPointerDown={() => setErrorMessage(null)}>
          <div className={styles.errorDialog} onPointerDown={e => e.stopPropagation()}>
            <div className={styles.errorTitle}>{t('generationErrorTitle')}</div>
            <div className={styles.errorBody}>{errorMessage}</div>
            <button className={styles.errorDismissBtn} onClick={() => setErrorMessage(null)}>
              {t('dismissButton')}
            </button>
          </div>
        </div>
      )}

      {historyOpen && (
        <GenerationHistoryPanel
          onPick={() => { setHistoryOpen(false); }}
          onDismiss={() => setHistoryOpen(false)}
          initialFilter="tts"
        />
      )}
    </div>
  );
}
